<script setup lang="ts">
import { useSelectedLogSorterStore, type Bin } from '../stores/selectedLogSorterStore';
import { storeToRefs } from 'pinia';
import { InputText, InputNumber, Fieldset, ToggleButton, Fluid, ToggleSwitch, InputGroup, InputGroupAddon } from 'primevue';
import { useTranslations } from 'shared/translations/composables/useTranslations.ts';


const store = useSelectedLogSorterStore();
const translations = useTranslations();

const {
  bins,
  operation,
} = storeToRefs(store);

function onWidthSpecifiedChanged(bin: Bin) {
  if (!bin.widthSpecified) {
    store.onBinChanged(true);
  }
}

function onLengthSpecifiedChanged(bin: Bin) {
  if (!bin.lengthSpecified) {
    store.onBinChanged(true);
  }
}

</script>

<template>

  <div class="bin-panel">

    <div v-bind:key="bin.name" v-for="bin in bins" class="field ">

      <Fieldset :legend="bin.name">
        <div class="bin">
          <div class="field col-span-2" :class="{ changed: operation === 'edit' && bin.hasChanged.identifier }">
            <label>{{ translations.logSorter.panel.identifier }}</label>
            <InputText fluid v-model:model-value="bin.identifier" @update:model-value="store.onBinChanged(true)"></InputText>
          </div>

          <div class="field col-span-2" :class="{ changed: operation === 'edit' && bin.hasChanged.width }">
            <label>{{ translations.generic.width }}</label>
            <InputGroup>
              <InputNumber v-model:model-value="bin.width" show-buttons button-layout="horizontal" :step="0.1"
                :disabled="!bin.widthSpecified"
                @update:model-value="() => { bin.widthSpecified = true; store.onBinChanged(true) }">
                <template #incrementicon>
                  <span class="pi pi-plus" />
                </template>
                <template #decrementicon>
                  <span class="pi pi-minus" />
                </template>
              </InputNumber>
              <ToggleButton
                v-model="bin.widthSpecified"
                @update:model-value="() => onWidthSpecifiedChanged(bin)"
                onLabel=""
                offLabel=""
                onIcon="pi pi-lock-open"
                offIcon="pi pi-lock"
                class="lock-button"
              />
            </InputGroup>
          </div>


          <div class="field col-span-2" :class="{ changed: operation === 'edit' && bin.hasChanged.length }">
            <label>{{ translations.generic.length }}</label>
            <InputGroup>
              <InputNumber v-model:model-value="bin.length" show-buttons button-layout="horizontal" :step="0.1"
                :disabled="!bin.lengthSpecified"
                @update:model-value="store.onBinChanged(true)">
                <template #incrementicon>
                  <span class="pi pi-plus" />
                </template>
                <template #decrementicon>
                  <span class="pi pi-minus" />
                </template>
              </InputNumber>
              <ToggleButton
                v-model="bin.lengthSpecified"
                @update:model-value="() => onLengthSpecifiedChanged(bin)"
                onLabel=""
                offLabel=""
                onIcon="pi pi-lock-open"
                offIcon="pi pi-lock"
                class="lock-button"
              />
            </InputGroup>
          </div>
          <div class="field" :class="{ changed: operation === 'edit' && bin.hasChanged.offsetY }">
            <label>{{ translations.logSorter.panel.offsetY }}</label>
            <InputNumber fluid v-model:model-value="bin.offsetY" show-buttons button-layout="horizontal" :step="0.1"
              @update:model-value="store.onBinChanged(true)">
              <template #incrementicon>
                <span class="pi pi-plus" />
              </template>
              <template #decrementicon>
                <span class="pi pi-minus" />
              </template>
            </InputNumber>
          </div>
          <div class="field" :class="{ changed: operation === 'edit' && bin.hasChanged.offsetX }">
            <label>{{ translations.logSorter.panel.offsetX }}</label>
            <InputNumber fluid v-model:model-value="bin.offsetX" show-buttons button-layout="horizontal" :step="0.1"
              @update:model-value="store.onBinChanged(true)">
              <template #incrementicon>
                <span class="pi pi-plus" />
              </template>
              <template #decrementicon>
                <span class="pi pi-minus" />
              </template>
            </InputNumber>
          </div>
        </div>
      </Fieldset>
    </div>
  </div>

</template>

<style scoped>
.bin-panel {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.bin {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Lock toggle button */
.lock-button {
    width: 3rem;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    background: var(--p-inputnumber-button-background) !important;
    border-color: var(--p-content-border-color) !important;
    color: var(--p-text-color) !important;
}

.lock-button.p-togglebutton-checked {
    background: var(--p-inputnumber-button-background) !important;
}

.lock-button :deep(.p-togglebutton) {
    width: 100%;
    height: 100%;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: center;
}

.lock-button :deep(.p-togglebutton-content) {
    background: var(--p-inputnumber-button-background) !important;
}

.lock-button :deep(.p-togglebutton-label) {
    display: none;
}

.lock-button :deep(.p-button-icon) {
    margin: 0;
    font-size: 1.25rem;
}


/* Connect InputNumber and ToggleButton visually */
:deep(.p-inputnumber-button.p-inputnumber-increment-button) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

:deep(.p-inputtext) {
    border-radius: 0 !important;
}
</style>
